/* eslint-disable react-hooks/exhaustive-deps */
import ButtonArrow from "@lhd/components/ButtonArrowDown";
import { getDisplaySize } from "@lhd/components/DisplaySizeSelector/selector";
import {
  BUTTON_HEIGHT,
  FONT_SIZE,
  FONT_SIZE_VALUE,
  ROUTES,
  ROW_HEIGHT_E_LEARNING,
  SCALE_BY_DISPLAY_SIZE,
} from "@lhd/constants";
import { user } from "@lhd/features/users/selector";
import {
  useDeleteMultipleMaintenanceNotification,
  useFetchMaintenanceNotification,
} from "@lhd/hooks";
import { DISPLAY_SIZES } from "@lhd/types";
import { checkDateInValid } from "@lhd/utility";
import { GridSizeChangedEvent } from "ag-grid-community";
import dayjs from "dayjs";
import { get } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isEqual, isEqualWith, sortBy } from "lodash";
import { useCascadingFilter } from "@lhd/hooks";
import {
  checkHasEmptyFilter,
  handleGetParamsFilterWithFieldKey,
} from "./fieldKeyMapping";
import useGetOptions from "./useGetOptions";
import {
  FILTER_PARAMS,
  columns as getColumnsDefault,
  WIDTH_OPERATOR,
} from "./utility";
import { parsePositiveIntWithDefault } from "@lhd/features/TrackingTime/until";
import CheckboxCustom from "@lhd/components/CheckboxCustom";
import { FilterInfo } from "@lhd/features/Apps/AppList/ManagementAppSheet/ManagementAppSheetList/AppSheetComponents/FilterIcon";

moment.locale("ja");

export type ReceivedProps = Record<string, any>;

const useMainContent = (props: ReceivedProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const gridRef: any = useRef();
  const columnsDefault = getColumnsDefault();
  const auth = useSelector(user);
  const [gridApi, setGridApi] = useState<any>(null);
  const [rowData, setRowData] = useState<any[]>([]);
  const [filterParams, setFilterParams] = useState<Record<string, any>>({});
  const [total, setTotal] = useState<number>(0);
  const [listSelected, setListSelected] = useState<number[]>([]);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [isCheckAll, setIsCheckAll] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
  const [modalMode, setModalMode] = useState<"create" | "view" | "edit">();
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const displaySize = useSelector(getDisplaySize) as
    | keyof typeof FONT_SIZE
    | keyof typeof BUTTON_HEIGHT;
  const height = BUTTON_HEIGHT[displaySize] ?? BUTTON_HEIGHT.small;
  const size = FONT_SIZE_VALUE[displaySize ?? DISPLAY_SIZES.SMALL];
  const rowHeight =
    ROW_HEIGHT_E_LEARNING[displaySize as keyof typeof ROW_HEIGHT_E_LEARNING];
  const page = parsePositiveIntWithDefault(searchParams.get("page"), 1);
  const pageSize = parsePositiveIntWithDefault(
    searchParams.get("per_page"),
    50,
  );

  const allColumnDefs = useMemo(() => {
    return columnsDefault.map((item: any) => ({
      ...item,
      field: item?.field,
    }));
  }, [columnsDefault]);

  const { filterInfo, setFilterInfo } = useCascadingFilter({
    columns: allColumnDefs,
    getColumnId: (col: any) => col.field,
  });

  const hasEmptyFilter = useMemo(
    () => checkHasEmptyFilter(filterInfo),
    [filterInfo],
  );

  const { options } = useGetOptions({ filterInfo });

  const {
    data: dataTable,
    isLoading: isMaintenanceNoticesLoading,
    isSuccess,
    isFetching: isMaintenanceNoticesFetching,
    refetch,
  } = useFetchMaintenanceNotification(page, pageSize, {
    ...filterParams,
    __BLOCK_API__: hasEmptyFilter,
  });

  const isLoading = isMaintenanceNoticesLoading && !hasEmptyFilter;
  const isFetching = isMaintenanceNoticesFetching && !hasEmptyFilter;

  const { mutate: mutateMultiple, isLoading: isDeleting } =
    useDeleteMultipleMaintenanceNotification();

  const navigate = useNavigate();

  const handleCancelModal = () => {
    setIsOpenModal(false);
    setSelectedId(undefined);
  };

  const handleOpenModal = () => {
    navigate(
      `/${ROUTES.MAINTENANCE_NOTIFICATION}/${ROUTES.CREATE}?pageMode=create`,
    );
    setIsOpenModal(true);
  };

  const handleViewDetail = (id: string) => {
    setModalMode("view");
    setSelectedId(id);
    setIsOpenModal(true);
  };

  const handleSuccess = () => {
    if (total + 1 > pageSize * page && modalMode === "create") {
      const next = new URLSearchParams(searchParams);
      next.set("page", `${page + 1}`);
      setSearchParams(next, { replace: true });
    } else {
      refetch();
    }
  };

  useEffect(() => {
    setIsFirstTime(false);
    if (!isFirstTime) refetch();
  }, [page, pageSize]);

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (!gridApi) return;
    gridApi.resetRowHeights();
    gridApi.refreshCells({ force: true });
    gridApi.ensureIndexVisible(0);
  }, [rowHeight, gridApi]);

  useEffect(() => {
    if (!filterInfo.filter) return;

    const params = handleGetParamsFilterWithFieldKey(filterInfo);
    const compareFilter = isEqualWith(
      filterParams,
      params,
      (value1, value2) => {
        if (Array.isArray(value1) && Array.isArray(value2)) {
          return isEqual(sortBy(value1), sortBy(value2));
        }
      },
    );

    if (compareFilter) return;

    setFilterParams(params);

    const next = new URLSearchParams(searchParams);
    next.set("page", "1");
    setSearchParams(next, { replace: true });
    handleCancelModal();
  }, [filterInfo, searchParams, setSearchParams, filterParams]);

  const onFilterUpdate = useCallback(() => {}, []);

  const handleDeleteMultipleCell = () => {
    const listIds: string[] = listSelected.map((id: number) => String(id));
    mutateMultiple(
      {
        listIds,
      },
      {
        onSuccess() {
          setIsDelete(false);
          setRowData([]);
          setListSelected([]);
          if (page > 1 && (total - listIds.length) % pageSize === 0) {
            const next = new URLSearchParams(searchParams);
            next.set("page", `${page - 1}`);
            setSearchParams(next, { replace: true });
          } else refetch();
        },
      },
    );
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      const newListSelected = rowData.map((element: any) => element?.id);
      setListSelected([...newListSelected]);
    } else {
      setListSelected([]);
    }
  };

  const SelectCheckAll = () => (
    <CheckboxCustom
      style={{ marginLeft: 8 }}
      type="checkbox"
      checked={isCheckAll}
      onChange={(e) => {
        const isChecked = e.target.checked;
        handleSelectAll(isChecked);
        setIsCheckAll(isChecked);
      }}
      displaySize={displaySize}
      label={{
        content: "全選択",
        style: { color: "#ffffff" },
      }}
    />
  );

  const onShowModalDelete = () => {
    setIsDelete(true);
  };
  const onCancelModalDelete = () => setIsDelete(false);

  const columnDefsSetting: any = columnsDefault.map(
    (item: any, index: number) => ({
      ...item,
      editable: false,
      filter: "agTextColumnFilter",
      unSortIcon: true,
      ...(index > 1 && {
        headerWithFilter: true,
        sortable: false,
      }),
      filterParams: {
        textMatcher: () => {
          return true;
        },
      },
      ...(index === 0 && {
        filter: false,
      }),
      ...(index === 1 && {
        filter: false,
        cellStyle: () => ({
          padding: 4,
          gap: 8,
        }),
        width: WIDTH_OPERATOR[displaySize],
        minWidth: WIDTH_OPERATOR[displaySize],
        maxWidth: WIDTH_OPERATOR[displaySize],
        headerComponentFramework: SelectCheckAll,
        cellRenderer: (params: any) => {
          const indexSelect = listSelected.findIndex(
            (idItem) => Number(idItem) === Number(params.data.id),
          );

          return (
            <>
              <CheckboxCustom
                type="checkbox"
                checked={indexSelect >= 0}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  if (isChecked) {
                    setListSelected((oldList) => {
                      const newList = [...oldList, Number(params?.data?.id)];
                      if (newList?.length === rowData?.length) {
                        setIsCheckAll(true);
                      }
                      return newList;
                    });
                  } else {
                    const newList = [...listSelected];
                    newList.splice(indexSelect, 1);
                    if (isCheckAll) {
                      setIsCheckAll(false);
                    }
                    setListSelected(newList);
                  }
                }}
                displaySize={displaySize}
              />

              <ButtonArrow
                name="下見"
                onClick={() => handleViewDetail(params.data.id)}
                fontSize={size - 1}
                height={height + 3}
              />
            </>
          );
        },
      }),
    }),
  );

  const onGridReady = (params: GridSizeChangedEvent) => {
    params.api.sizeColumnsToFit();
    setGridApi(params.api);
  };

  useEffect(() => {
    if (hasEmptyFilter) {
      setRowData([]);
      setTotal(0);
      return;
    }
    if (isLoading || isFetching) {
      setRowData([]);
    } else {
      if (isSuccess) {
        const dataFormat = dataTable?.data?.map((item: any, index: number) => {
          return {
            id: item?.id,
            index: index,
            isChecked: false,
            title: item?.title,
            main_notice: item?.main_notice,
            maintenance_start_at: dayjs(item?.maintenance_start_at).format(
              "DD/MM/YYYY HH:mm",
            ),
            maintenance_end_at: dayjs(item?.maintenance_end_at).format(
              "DD/MM/YYYY HH:mm",
            ),
          };
        });
        setRowData(dataFormat);
        setTotal(+dataTable.meta.total);
      }
    }
  }, [
    auth?.workers,
    isFetching,
    isLoading,
    isSuccess,
    dataTable,
    hasEmptyFilter,
  ]);

  useEffect(() => {
    return () => {
      setListSelected([]);
      setIsCheckAll(false);
    };
  }, [rowData?.length]);

  useEffect(() => {
    if (!gridApi) return;

    const updatedDefs = gridApi
      .getColumnDefs()
      .map((colDef: any, i: number) => {
        if (i === 1) {
          return {
            ...colDef,
            width: WIDTH_OPERATOR[displaySize],
            minWidth: WIDTH_OPERATOR[displaySize],
            maxWidth: WIDTH_OPERATOR[displaySize],
          };
        }
        return colDef;
      });

    gridApi.setColumnDefs(updatedDefs);
    gridApi.sizeColumnsToFit();
  }, [displaySize, gridApi]);

  useEffect(() => {
    if (hasEmptyFilter || isLoading || isFetching || !isSuccess) return;

    const currentTotal = +(dataTable?.meta?.total ?? 0);
    const totalPages = Math.max(Math.ceil(currentTotal / pageSize), 1);
    const isPageOutOfRange = page > totalPages;
    const newPage = isPageOutOfRange ? totalPages : page;

    const next = new URLSearchParams(searchParams);
    currentTotal > 0
      ? next.set("page", `${newPage}`)
      : ["page", "per_page"].forEach((key) => next.delete(key));

    if (next.toString() === searchParams.toString()) return;

    setSearchParams(next, { replace: true });
  }, [
    isFetching,
    isLoading,
    isSuccess,
    dataTable,
    page,
    pageSize,
    searchParams,
    setSearchParams,
  ]);

  return {
    ...props,
    columnDefs: columnDefsSetting,
    rowData,
    gridApi,
    gridRef,
    onGridReady,
    onFilterUpdate,
    isLoading,
    isFetching,
    pageSize,
    total,
    page,
    isDelete,
    isDeleting,
    listSelected,
    onShowModalDelete,
    onCancelModalDelete,
    handleDeleteMultipleCell,
    handleCancelModal,
    handleOpenModal,
    isOpenModal,
    modalMode,
    selectedId,
    displaySize,
    rowHeight,
    height,
    size,
    onSuccess: handleSuccess,
    filterInfo,
    setFilterInfo,
    hasEmptyFilter,
    options,
  };
};

export type Props = ReturnType<typeof useMainContent>;

export default useMainContent;
